// src/pages/cadastros/Prestadores/EditarPrestador.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useIsMobile } from "../../../hooks/useIsMobile";

import LayoutDesktop from "../../../layouts/LayoutDesktop";
import LayoutMobile from "../../../layouts/LayoutMobile";

import { db } from "../../../config/firebase";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";

function toTextoEspecialidades(arr) {
  if (Array.isArray(arr) && arr.length) return arr.join(", ");
  if (typeof arr === "string") return arr;
  return "";
}

function parseEspecialidades(texto) {
  const raw = (texto || "").trim();
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function EditarPrestador() {
  const { tema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isMobile = useIsMobile(900);

  const [sidebarAberta, setSidebarAberta] = useState(false);

  const [codigo, setCodigo] = useState("");
  const [nomeFantasia, setNomeFantasia] = useState("");
  const [razaoSocial, setRazaoSocial] = useState("");
  const [cnpj, setCnpj] = useState("");

  const [contatoNome, setContatoNome] = useState("");
  const [telefone, setTelefone] = useState("");
  const [email, setEmail] = useState("");

  const [especialidadesTexto, setEspecialidadesTexto] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [ativo, setAtivo] = useState(true);

  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");

  const inputStyle = {
    width: "100%",
    padding: "9px 11px",
    marginTop: 4,
    borderRadius: 8,
    border: "1px solid #334155",
    boxSizing: "border-box",
  };

  useEffect(() => {
    async function carregar() {
      if (!usuarioLogado) return;

      setCarregando(true);
      setErro("");

      try {
        const ref = doc(db, "prestadores", id);
        const snap = await getDoc(ref);

        if (!snap.exists()) {
          setErro("Prestador não encontrado.");
          return;
        }

        const data = snap.data() || {};

        // segurança simples: se tiver grupoId e não bater, bloqueia
        if (usuarioLogado?.grupoId && data?.grupoId && data.grupoId !== usuarioLogado.grupoId) {
          setErro("Sem permissão para editar este prestador (grupo diferente).");
          return;
        }

        setCodigo(data.codigo || "");
        setNomeFantasia(data.nomeFantasia || "");
        setRazaoSocial(data.razaoSocial || "");
        setCnpj(data.cnpj || "");

        setContatoNome(data.contatoNome || "");
        setTelefone(data.telefone || "");
        setEmail(data.email || "");

        setEspecialidadesTexto(toTextoEspecialidades(data.especialidades));
        setObservacoes(data.observacoes || "");
        setAtivo(data.ativo !== false);
      } catch (e) {
        console.error("Erro ao carregar prestador:", e);
        setErro("Erro ao carregar prestador.");
      } finally {
        setCarregando(false);
      }
    }

    if (id) carregar();
  }, [id, usuarioLogado]);

  const handleSalvar = async (e) => {
    e.preventDefault();
    setErro("");
    setSucesso("");

    if (!nomeFantasia.trim()) {
      setErro("Informe o Nome do Prestador.");
      return;
    }

    setSalvando(true);

    try {
      const ref = doc(db, "prestadores", id);

      const especialidades = parseEspecialidades(especialidadesTexto);

      await updateDoc(ref, {
        nomeFantasia: nomeFantasia.trim(),
        razaoSocial: razaoSocial.trim() || null,
        cnpj: cnpj.trim() || null,

        contatoNome: contatoNome.trim() || null,
        telefone: telefone.trim() || null,
        email: email.trim() || null,

        especialidades,
        observacoes: observacoes.trim() || null,
        ativo: !!ativo,

        updatedAt: serverTimestamp(),
        atualizadoPor: usuarioLogado?.uid || null,
      });

      setSucesso("Prestador atualizado com sucesso.");
      setTimeout(() => navigate("/cadastros/prestadores"), 350);
    } catch (e2) {
      console.error("Erro ao salvar prestador:", e2);
      setErro(e2?.message || "Erro ao salvar prestador.");
    } finally {
      setSalvando(false);
    }
  };

  const Layout = isMobile ? LayoutMobile : LayoutDesktop;

  const conteudo = (
    <section style={{ width: "100%" }}>
      <div
        className={isMobile ? "m-card" : "pc-card"}
        style={{
          width: "100%",
          padding: isMobile ? 16 : "16px 40px",
          boxSizing: "border-box",
        }}
      >
        {!isMobile && (
          <header style={{ marginBottom: 12 }}>
            <h2 style={{ margin: 0, fontSize: 22 }}>Editar Prestador de Serviço</h2>
            <p style={{ margin: "6px 0 0", opacity: 0.8 }}>
              Código: <strong>{codigo || "-"}</strong>
            </p>
          </header>
        )}

        {erro && (
          <div
            style={{
              marginTop: isMobile ? 0 : 12,
              marginBottom: 12,
              padding: 8,
              borderRadius: 8,
              background: "#7f1d1d",
              color: "#fecaca",
              whiteSpace: "pre-wrap",
            }}
          >
            {erro}
          </div>
        )}

        {sucesso && (
          <div
            style={{
              marginTop: isMobile ? 0 : 12,
              marginBottom: 12,
              padding: 8,
              borderRadius: 8,
              background: "#064e3b",
              color: "#d1fae5",
              whiteSpace: "pre-wrap",
            }}
          >
            {sucesso}
          </div>
        )}

        {carregando ? (
          <div style={{ opacity: 0.8 }}>Carregando...</div>
        ) : (
          <form
            onSubmit={handleSalvar}
            style={{
              marginTop: isMobile ? 0 : 12,
              maxWidth: isMobile ? "100%" : 720,
              marginLeft: "auto",
              marginRight: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <label>
              <span style={{ fontSize: 14 }}>Nome Fantasia *</span>
              <input
                type="text"
                value={nomeFantasia}
                onChange={(e) => setNomeFantasia(e.target.value)}
                className={isMobile ? "m-input" : ""}
                style={{ ...inputStyle, color: "inherit" }}
              />
            </label>

            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
              <label>
                <span style={{ fontSize: 14 }}>Razão Social</span>
                <input
                  type="text"
                  value={razaoSocial}
                  onChange={(e) => setRazaoSocial(e.target.value)}
                  className={isMobile ? "m-input" : ""}
                  style={{ ...inputStyle, color: "inherit" }}
                />
              </label>

              <label>
                <span style={{ fontSize: 14 }}>CNPJ</span>
                <input
                  type="text"
                  value={cnpj}
                  onChange={(e) => setCnpj(e.target.value)}
                  className={isMobile ? "m-input" : ""}
                  style={{ ...inputStyle, color: "inherit" }}
                />
              </label>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 12 }}>
              <label>
                <span style={{ fontSize: 14 }}>Contato</span>
                <input
                  type="text"
                  value={contatoNome}
                  onChange={(e) => setContatoNome(e.target.value)}
                  className={isMobile ? "m-input" : ""}
                  style={{ ...inputStyle, color: "inherit" }}
                />
              </label>

              <label>
                <span style={{ fontSize: 14 }}>Telefone</span>
                <input
                  type="text"
                  value={telefone}
                  onChange={(e) => setTelefone(e.target.value)}
                  className={isMobile ? "m-input" : ""}
                  style={{ ...inputStyle, color: "inherit" }}
                />
              </label>

              <label>
                <span style={{ fontSize: 14 }}>Email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={isMobile ? "m-input" : ""}
                  style={{ ...inputStyle, color: "inherit" }}
                />
              </label>
            </div>

            <label>
              <span style={{ fontSize: 14 }}>Especialidades</span>
              <textarea
                value={especialidadesTexto}
                onChange={(e) => setEspecialidadesTexto(e.target.value)}
                className={isMobile ? "m-input" : ""}
                style={{ ...inputStyle, minHeight: 70, color: "inherit" }}
              />
            </label>

            <label>
              <span style={{ fontSize: 14 }}>Observações</span>
              <textarea
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                className={isMobile ? "m-input" : ""}
                style={{ ...inputStyle, minHeight: 80, color: "inherit" }}
              />
            </label>

            <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <input
                type="checkbox"
                checked={ativo}
                onChange={(e) => setAtivo(e.target.checked)}
              />
              <span>Ativo</span>
            </label>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 6 }}>
              <button
                type="button"
                className="pc-btn-secundario"
                onClick={() => navigate("/cadastros/prestadores")}
                disabled={salvando}
              >
                Cancelar
              </button>

              <button type="submit" className="pc-btn-primario" disabled={salvando}>
                {salvando ? "Salvando..." : "Salvar prestador"}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );

  return (
    <Layout
      tema={tema}
      titulo="Editar Prestador"
      sidebarAberta={sidebarAberta}
      onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
    >
      {conteudo}
    </Layout>
  );
}

export default EditarPrestador;
